import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Bot, CheckCircle2, AlertTriangle, Clock, ChevronDown } from "lucide-react";

// Tooth shape SVG paths for different tooth types
const toothShapes = {
  molar: { w: 36, h: 38, paths: { O: "M10,12 L26,12 L26,26 L10,26 Z", B: "M10,12 L26,12 L30,4 L6,4 Z", L: "M10,26 L26,26 L30,34 L6,34 Z", M: "M10,12 L10,26 L6,34 L6,4 Z", D: "M26,12 L26,26 L30,34 L30,4 Z" } },
  premolar: { w: 32, h: 36, paths: { O: "M10,11 L22,11 L22,25 L10,25 Z", B: "M10,11 L22,11 L26,4 L6,4 Z", L: "M10,25 L22,25 L26,32 L6,32 Z", M: "M10,11 L10,25 L6,32 L6,4 Z", D: "M22,11 L22,25 L26,32 L26,4 Z" } },
  canine: { w: 28, h: 36, paths: { O: "M9,11 L19,11 L19,25 L9,25 Z", B: "M9,11 L19,11 L22,4 L6,4 Z", L: "M9,25 L19,25 L22,32 L6,32 Z", M: "M9,11 L9,25 L6,32 L6,4 Z", D: "M19,11 L19,25 L22,32 L22,4 Z" } },
  incisor: { w: 26, h: 34, paths: { O: "M8,10 L18,10 L18,24 L8,24 Z", B: "M8,10 L18,10 L21,4 L5,4 Z", L: "M8,24 L18,24 L21,30 L5,30 Z", M: "M8,10 L8,24 L5,30 L5,4 Z", D: "M18,10 L18,24 L21,30 L21,4 Z" } },
};

const getToothType = (num) => {
  const n = num <= 16 ? num : num - 16;
  if (n <= 2 || n === 16 || n === 15) return "molar";
  if (n <= 4 || n === 14 || n === 13) return "premolar";
  if (n === 5 || n === 12) return "canine";
  return "incisor";
};

const getToothLabel = (num) => {
  const names = { 1:"3rd Molar",2:"2nd Molar",3:"1st Molar",4:"2nd Premolar",5:"1st Premolar",6:"Canine",7:"Lateral Incisor",8:"Central Incisor",9:"Central Incisor",10:"Lateral Incisor",11:"Canine",12:"1st Premolar",13:"2nd Premolar",14:"1st Molar",15:"2nd Molar",16:"3rd Molar" };
  const n = num <= 16 ? num : 33 - num;
  return names[n] || "";
};

// Conditions data for the demo
const conditions = {
  3: { surfaces: { O: "#3b82f6", B: "#3b82f6" }, note: "Amalgam restoration", date: "01/15/2025", status: "existing" },
  5: { surfaces: { O: "#8b5cf6" }, note: "Composite restoration", date: "03/22/2025", status: "existing" },
  8: { surfaces: { M: "#f59e0b" }, note: "Caries detected – AI flagged", date: "02/10/2026", status: "planned", ai: true },
  12: { surfaces: { O: "#3b82f6", D: "#3b82f6" }, note: "Amalgam restoration", date: "06/10/2024", status: "existing" },
  14: { surfaces: { O: "#ef4444", M: "#ef4444" }, note: "Crown recommended – AI detected fracture line", date: "02/14/2026", status: "planned", ai: true },
  19: { surfaces: { O: "#3b82f6", B: "#3b82f6", D: "#3b82f6" }, note: "Large amalgam restoration", date: "09/05/2023", status: "existing" },
  20: { surfaces: { O: "#8b5cf6" }, note: "Composite restoration", date: "11/18/2024", status: "existing" },
  24: { surfaces: { M: "#f59e0b", O: "#f59e0b" }, note: "Early caries – AI flagged for watch", date: "02/14/2026", status: "watch", ai: true },
  30: { surfaces: { O: "#3b82f6", D: "#3b82f6" }, note: "Amalgam restoration", date: "04/20/2024", status: "existing" },
  31: { surfaces: { B: "#ef4444" }, note: "Cervical abrasion noted", date: "02/14/2026", status: "planned" },
};

const missingTeeth = [1, 16, 17, 32];

const upperTeeth = Array.from({ length: 16 }, (_, i) => i + 1);
const lowerTeeth = Array.from({ length: 16 }, (_, i) => 32 - i);

const Tooth = ({ number, isUpper, selected, onSelect, delay }) => {
  const missing = missingTeeth.includes(number);
  const condition = conditions[number];
  const type = getToothType(number);
  const shape = toothShapes[type];
  const surfaces = ["B", "M", "O", "D", "L"];

  return (
    <motion.div
      initial={{ opacity: 0, y: isUpper ? -8 : 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: delay, type: "spring", stiffness: 300, damping: 25 }}
      className="flex flex-col items-center gap-0.5 cursor-pointer group"
      onClick={() => onSelect(number)}
    >
      {isUpper && <span className={`text-[8px] font-bold ${selected ? "text-blue-600" : "text-slate-400"} group-hover:text-blue-500 transition-colors`}>{number}</span>}
      <motion.div
        whileHover={{ scale: 1.15 }}
        whileTap={{ scale: 0.95 }}
        className={`relative rounded-md transition-all duration-200 ${selected ? "ring-2 ring-blue-500 ring-offset-1 shadow-md shadow-blue-500/20" : "hover:ring-1 hover:ring-slate-300"} ${missing ? "opacity-30" : ""}`}
      >
        <svg width={shape.w * 0.85} height={shape.h * 0.85} viewBox={`0 0 ${shape.w} ${shape.h}`}>
          {surfaces.map((s) => {
            const fill = condition?.surfaces?.[s] || (missing ? "#e2e8f0" : "#f8fafc");
            const stroke = missing ? "#cbd5e1" : (condition?.surfaces?.[s] ? "#ffffff" : "#cbd5e1");
            return (
              <path key={s} d={shape.paths[s]} fill={fill} stroke={stroke} strokeWidth="0.8"
                className="transition-colors duration-200" opacity={missing ? 0.4 : 1} />
            );
          })}
          {condition?.ai && (
            <circle cx={shape.w / 2} cy={shape.h / 2} r="3" fill="#f59e0b" stroke="#fff" strokeWidth="1">
              <animate attributeName="r" values="3;4;3" dur="2s" repeatCount="indefinite" />
            </circle>
          )}
        </svg>
        {missing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-[6px] font-bold text-slate-400">X</span>
          </div>
        )}
      </motion.div>
      {!isUpper && <span className={`text-[8px] font-bold ${selected ? "text-blue-600" : "text-slate-400"} group-hover:text-blue-500 transition-colors`}>{number}</span>}
    </motion.div>
  );
};

const OdontogramChart = () => {
  const [selectedTooth, setSelectedTooth] = useState(14);
  const [showAiInsight, setShowAiInsight] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setShowAiInsight(true), 1500);
    return () => clearTimeout(t);
  }, []);

  const sel = conditions[selectedTooth];

  const legendItems = [
    { color: "#3b82f6", label: "Existing Restoration" },
    { color: "#8b5cf6", label: "Composite" },
    { color: "#f59e0b", label: "AI Flagged – Watch" },
    { color: "#ef4444", label: "Treatment Planned" },
  ];

  const procedureHistory = [
    { date: "02/14/2026", code: "D0120", desc: "Periodic oral evaluation", tooth: "-", prov: "Dr. Smith", status: "completed" },
    { date: "02/14/2026", code: "D0274", desc: "Bitewings - four films", tooth: "-", prov: "Dr. Smith", status: "completed" },
    { date: "01/15/2025", code: "D2150", desc: "Amalgam - 2 surfaces", tooth: "#3", prov: "Dr. Smith", status: "completed" },
    { date: "03/22/2025", code: "D2391", desc: "Composite - 1 surface", tooth: "#5", prov: "Dr. Lee", status: "completed" },
    { date: "02/14/2026", code: "D2740", desc: "Crown - porcelain/ceramic", tooth: "#14", prov: "Dr. Smith", status: "planned" },
  ];

  const perioData = [
    { label: "BOP Sites", value: "12%", color: "text-emerald-600", bg: "bg-emerald-50" },
    { label: "Avg Pocket Depth", value: "2.8mm", color: "text-blue-600", bg: "bg-blue-50" },
    { label: "Recession Sites", value: "3", color: "text-amber-600", bg: "bg-amber-50" },
    { label: "Risk Level", value: "Low", color: "text-emerald-600", bg: "bg-emerald-50" },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* Chart Header */}
      <div className="flex items-center justify-between px-5 py-2.5 bg-white border-b border-slate-200 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Patient Chart</h2>
            <p className="text-[10px] text-slate-400">James Wilson • DOB: 07/28/1972 • Chart #4502</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-slate-100 rounded-lg p-0.5">
            {["Odontogram", "Perio", "History"].map((tab) => (
              <button key={tab} className={`text-[10px] font-semibold px-3 py-1 rounded-md transition-all ${tab === "Odontogram" ? "bg-white text-blue-700 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>{tab}</button>
            ))}
          </div>
          <motion.div whileHover={{ scale: 1.02 }} className="flex items-center gap-1.5 text-[10px] font-bold text-violet-600 px-2.5 py-1.5 rounded-lg bg-violet-50 border border-violet-100 cursor-pointer">
            <Sparkles className="w-3 h-3" /> AI Analysis
          </motion.div>
        </div>
      </div>

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Odontogram Main */}
        <div className="flex-1 flex flex-col p-4 overflow-y-auto">
          {/* Odontogram */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 mb-3">
            {/* Legend */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                {legendItems.map((item) => (
                  <div key={item.label} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: item.color }} />
                    <span className="text-[9px] font-medium text-slate-500">{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="text-[9px] text-slate-400 font-medium">Click any tooth for details</div>
            </div>

            {/* Upper Arch */}
            <div className="mb-2">
              <div className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 text-center">Maxillary (Upper)</div>
              <div className="flex items-end justify-center gap-[3px]">
                {upperTeeth.map((num, i) => (
                  <Tooth key={num} number={num} isUpper={true} selected={selectedTooth === num} onSelect={setSelectedTooth} delay={0.3 + i * 0.03} />
                ))}
              </div>
            </div>

            {/* Midline */}
            <div className="flex items-center gap-2 my-2">
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
              <span className="text-[7px] font-bold text-slate-400 uppercase tracking-widest">R — Midline — L</span>
              <div className="flex-1 h-px bg-gradient-to-r from-transparent via-slate-300 to-transparent" />
            </div>

            {/* Lower Arch */}
            <div>
              <div className="flex items-start justify-center gap-[3px]">
                {lowerTeeth.map((num, i) => (
                  <Tooth key={num} number={num} isUpper={false} selected={selectedTooth === num} onSelect={setSelectedTooth} delay={0.5 + i * 0.03} />
                ))}
              </div>
              <div className="text-[8px] font-bold uppercase tracking-widest text-slate-400 mt-1.5 text-center">Mandibular (Lower)</div>
            </div>
          </motion.div>

          {/* Bottom Row: Perio Summary + Procedure History */}
          <div className="grid grid-cols-12 gap-3 min-h-0">
            {/* Perio Summary */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }}
              className="col-span-4 bg-white rounded-xl border border-slate-200 shadow-sm p-3">
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Perio Summary</div>
              <div className="space-y-2">
                {perioData.map((item) => (
                  <div key={item.label} className="flex items-center justify-between">
                    <span className="text-[10px] text-slate-600">{item.label}</span>
                    <span className={`text-[10px] font-bold ${item.color} px-1.5 py-0.5 rounded ${item.bg}`}>{item.value}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Procedure History */}
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
              className="col-span-8 bg-white rounded-xl border border-slate-200 shadow-sm p-3 overflow-hidden">
              <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400 mb-2">Recent Procedures</div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {["Date","Code","Description","Tooth","Provider","Status"].map((h) => (
                        <th key={h} className="text-[8px] font-bold uppercase tracking-wider text-slate-400 py-1 px-1.5 text-left">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {procedureHistory.map((row, i) => (
                      <motion.tr key={i} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 + i * 0.08 }}
                        className="border-b border-slate-50 hover:bg-slate-50 transition-colors">
                        <td className="text-[9px] text-slate-600 py-1.5 px-1.5">{row.date}</td>
                        <td className="text-[9px] font-mono font-bold text-slate-700 py-1.5 px-1.5">{row.code}</td>
                        <td className="text-[9px] text-slate-600 py-1.5 px-1.5">{row.desc}</td>
                        <td className="text-[9px] text-slate-600 py-1.5 px-1.5">{row.tooth}</td>
                        <td className="text-[9px] text-slate-600 py-1.5 px-1.5">{row.prov}</td>
                        <td className="py-1.5 px-1.5">
                          <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${row.status === "completed" ? "bg-emerald-50 text-emerald-600 border border-emerald-100" : "bg-amber-50 text-amber-600 border border-amber-100"}`}>
                            {row.status}
                          </span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Right Panel: Tooth Detail + AI */}
        <div className="w-[220px] bg-white border-l border-slate-200 flex flex-col py-3 overflow-y-auto flex-shrink-0">
          <div className="px-3 space-y-3">
            {/* Selected Tooth Detail */}
            <motion.div key={selectedTooth} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
              className="p-2.5 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[8px] font-bold uppercase tracking-wider text-blue-500">Tooth #{selectedTooth}</span>
                {sel?.ai && <span className="flex items-center gap-0.5 text-[8px] font-bold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100"><Sparkles className="w-2.5 h-2.5" /> AI</span>}
              </div>
              <div className="text-[11px] font-bold text-slate-800">{getToothLabel(selectedTooth)}</div>
              {missingTeeth.includes(selectedTooth) ? (
                <div className="text-[9px] text-slate-500 mt-1">Missing tooth</div>
              ) : sel ? (
                <>
                  <div className="text-[9px] text-slate-600 mt-1">{sel.note}</div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Clock className="w-2.5 h-2.5 text-slate-400" />
                    <span className="text-[9px] text-slate-500">{sel.date}</span>
                  </div>
                  <div className="mt-1.5">
                    <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded-full ${sel.status === "existing" ? "bg-blue-100 text-blue-700" : sel.status === "planned" ? "bg-red-50 text-red-600 border border-red-100" : "bg-amber-50 text-amber-600 border border-amber-100"}`}>
                      {sel.status === "existing" ? "Existing" : sel.status === "planned" ? "Treatment Planned" : "Watch"}
                    </span>
                  </div>
                  <div className="mt-2 flex gap-1">
                    {Object.keys(sel.surfaces).map((s) => (
                      <span key={s} className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-white border border-slate-200 text-slate-600">{s}</span>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-[9px] text-emerald-600 font-medium mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Healthy – no findings</div>
              )}
            </motion.div>

            {/* AI Insights */}
            <AnimatePresence>
              {showAiInsight && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  className="p-2.5 rounded-xl bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-100">
                  <div className="flex items-center gap-1.5 mb-2">
                    <div className="w-5 h-5 rounded-md bg-gradient-to-br from-violet-600 to-purple-600 flex items-center justify-center">
                      <Bot className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-[9px] font-bold uppercase tracking-wider text-violet-600">AI Insights</span>
                  </div>
                  <div className="space-y-2">
                    {[
                      { icon: AlertTriangle, color: "text-amber-500", text: "Tooth #8: Early interproximal caries detected on bitewing — recommend monitoring" },
                      { icon: AlertTriangle, color: "text-red-500", text: "Tooth #14: Fracture line detected on periapical — crown recommended" },
                      { icon: CheckCircle2, color: "text-emerald-500", text: "Perio risk assessment: Low — stable pocket depths across all quadrants" },
                    ].map((insight, i) => (
                      <motion.div key={i} initial={{ opacity: 0, x: 5 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1.8 + i * 0.2 }}
                        className="flex items-start gap-1.5 p-1.5 rounded-lg bg-white/60">
                        <insight.icon className={`w-3 h-3 ${insight.color} mt-0.5 shrink-0`} />
                        <span className="text-[9px] text-slate-600 leading-relaxed">{insight.text}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Quick Actions */}
            <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200">
              <div className="text-[8px] font-bold uppercase tracking-wider text-slate-400 mb-2">Quick Actions</div>
              <div className="space-y-1.5">
                {["Add Procedure", "Create Treatment Plan", "Add Clinical Note", "Request X-Ray"].map((action, i) => (
                  <motion.button key={action} whileHover={{ x: 2 }} className="w-full text-left text-[10px] font-medium text-slate-600 hover:text-blue-600 py-1 px-2 rounded-lg hover:bg-white transition-all flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-400" /> {action}
                  </motion.button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OdontogramChart;
